import { z } from "zod";
import {
	StudentAPIWrapperSchema,
	StudentsListAPISchema,
	StudentAPIResponseSchema,
	transformAPIStudentToUIStudent,
} from "./student.schema";
import { type StudentUI } from "./data-service";
import { AuthenticatedAPIClient } from "./authenticated-api";
import { parseAPIResponse } from "./parse-api-response";
import { Toast, ErrorMessages } from "./utils";

type StudentAPIResponse = z.infer<typeof StudentAPIResponseSchema>;

// Relative on purpose: the API is served from the same origin as the app (the
// web container's nginx proxies /v1 to it), so there is no host to configure
// and no CORS in play.
export const API_BASE_URL = "/v1";

export interface APIResponse<T> {
	success: boolean;
	message: string;
	data?: T;
	code?: string;
	status?: number;
}

export interface PaginationInfo {
	page: number;
	limit: number;
	total: number;
	totalPages: number;
}

export interface StudentsListResponse {
	students: StudentAPIResponse[];
	pagination: PaginationInfo;
}

// Fetch student by MSSV
export async function fetchStudentById(mssv: string): Promise<StudentUI | null> {
	try {
		const apiResponse = await AuthenticatedAPIClient.get<StudentAPIResponse>(`/students/${mssv}`);

		if (!apiResponse.success || !apiResponse.data) {
			if (apiResponse.message?.includes("not found")) {
				return null; // Student not found
			}
			Toast.error(apiResponse.message || ErrorMessages.UNKNOWN_ERROR);
			throw new Error(apiResponse.message || "Failed to fetch student data");
		}

		// Validate response data with Zod schema
		const validatedResponse = parseAPIResponse(
			StudentAPIWrapperSchema,
			apiResponse,
			"GET /students/:mssv",
		);
		if (!validatedResponse.success || !validatedResponse.data) {
			Toast.error(ErrorMessages.VALIDATION_ERROR);
			throw new Error("Invalid API response structure");
		}

		// Convert to UI format
		return transformAPIStudentToUIStudent(validatedResponse.data);
	} catch (error) {
		console.error("Error fetching student:", error);
		if (error instanceof Error && error.message.includes("Authentication required")) {
			throw error; // Re-throw auth errors (already handled by AuthenticatedAPIClient)
		}
		// Network or parsing errors
		if (error instanceof Error && error.message.includes("Failed to fetch")) {
			Toast.error(ErrorMessages.NETWORK_ERROR);
		}
		throw error;
	}
}

// Fetch all students with pagination
export async function fetchAllStudents(
	page: number = 1,
	limit: number = 10,
): Promise<{ students: StudentUI[]; pagination: PaginationInfo }> {
	try {
		const apiResponse = await AuthenticatedAPIClient.get<StudentsListResponse>(
			`/students?page=${page}&limit=${limit}`,
		);

		if (!apiResponse.success || !apiResponse.data) {
			Toast.error(apiResponse.message || ErrorMessages.UNKNOWN_ERROR);
			throw new Error(apiResponse.message || "Failed to fetch students data");
		}

		// Validate and convert students data
		const validatedResponse = parseAPIResponse(StudentsListAPISchema, apiResponse, "GET /students");
		if (!validatedResponse.success || !validatedResponse.data) {
			Toast.error(ErrorMessages.VALIDATION_ERROR);
			throw new Error("Invalid API response structure");
		}

		const uiStudents = validatedResponse.data.students.map((student) =>
			transformAPIStudentToUIStudent(student),
		);

		return {
			students: uiStudents,
			pagination: apiResponse.data.pagination,
		};
	} catch (error) {
		console.error("Error fetching students:", error);
		if (error instanceof Error && error.message.includes("Authentication required")) {
			throw error; // Re-throw auth errors (already handled by AuthenticatedAPIClient)
		}
		// Network errors
		if (error instanceof Error && error.message.includes("Failed to fetch")) {
			Toast.error(ErrorMessages.NETWORK_ERROR);
		}
		throw error;
	}
}

// Search students by various criteria
export async function searchStudents(query: string): Promise<StudentUI[]> {
	try {
		// In a real implementation, this would be a proper search endpoint
		// For now, we'll search by MSSV if the query looks like an MSSV
		if (query.match(/^[A-Z0-9]+$/)) {
			const student = await fetchStudentById(query);
			return student ? [student] : [];
		}

		// Otherwise, we could implement a text search endpoint
		// For now, return empty array
		return [];
	} catch (error) {
		console.error("Error searching students:", error);
		return [];
	}
}

// Health check for API
export async function checkAPIHealth(): Promise<boolean> {
	try {
		// Health endpoint doesn't require authentication
		const response = await fetch(`${API_BASE_URL}/health`, {
			method: "GET",
			timeout: 5000,
		} as RequestInit & { timeout: number });

		return response.ok;
	} catch (error) {
		console.error("API health check failed:", error);
		Toast.error(ErrorMessages.NETWORK_ERROR, "Không thể kiểm tra trạng thái API");
		return false;
	}
}
